import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { useQueryClient } from "@tanstack/react-query";

import FetchClients, { ClientOutput } from "../client";
import { UTILITY_ACCOUNTS_KEY } from "./accounts";

export type UtilityCredential =
  ClientOutput["utilities"]["UtilityCredentialExtended"];

export type CreateUtilityCredentialRequest =
  ClientOutput["utilities"]["CreateUtilityCredential"];

export type UpdateUtilityCredentialRequest =
  ClientOutput["utilities"]["UpdateUtilityCredential"];

export type UtilityAccountRequest =
  ClientOutput["utilities"]["UtilityAccountRequest"];

export type AccountToSelect =
  ClientOutput["utilities"]["ArcadiaAccountToSelect"];

export function hasInvalidCredentialError(
  statusDetail: UtilityCredential["status_detail"]
) {
  return (
    statusDetail &&
    ["INVALID_CREDENTIALS", "LOGIN_FAILURE"].includes(statusDetail)
  );
}

const project = {
  useQueryAll,
  useQueryAccounts: useQueryAccounts,
  mutations: {
    usePost,
    usePut,
    usePostAccounts,
    useDelete,
  },
} as const;

export default project;

const UTILITY_CREDENTIALS_KEY = "utilities/credentials";

// endpoint requires {feature: "Utilities", access: "read"}
async function getCredentials(
  siteId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.utilities.GET(
    "/{site_id}/credentials",
    {
      params: {
        path: {
          site_id: siteId,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (data) return data;
  throw new Error(error);
}

function useQueryAll(siteId: number, poll?: boolean) {
  return useQueryWithAccessCheckLog<UtilityCredential[]>(
    ["Utilities", "read"],
    (checkFeatureAccessHeader) =>
      getCredentials(siteId, checkFeatureAccessHeader),
    {
      queryKey: [UTILITY_CREDENTIALS_KEY, siteId],
      refetchInterval: poll ? 5000 : undefined,
    }
  );
}

// endpoint requires {feature: "Utilities", access: "edit"}
async function postCredential(
  siteId: number,
  body: CreateUtilityCredentialRequest,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.utilities.POST(
    "/{site_id}/credentials",
    {
      body,
      params: {
        path: {
          site_id: siteId,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (data) return data;
  throw new Error(error);
}

function usePost(siteId: number) {
  const queryClient = useQueryClient();

  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (body: CreateUtilityCredentialRequest, checkFeatureAccessHeader) =>
      postCredential(siteId, body, checkFeatureAccessHeader),
    {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: [UTILITY_CREDENTIALS_KEY, siteId],
        }),
      onError: (e) => {
        throw new Error(
          `Unable to create new utility credential for site: ${siteId}
        ${(e as Error)?.message || "Unknown Error"}`
        );
      },
    }
  );
}

// endpoint requires {feature: "Utilities", access: "edit"}
async function putCredential(
  siteId: number,
  credentialId: number,
  body: UpdateUtilityCredentialRequest,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { error } = await FetchClients.utilities.PUT(
    "/{site_id}/credentials/{credential_id}",
    {
      body,
      params: {
        path: {
          site_id: siteId,
          credential_id: credentialId,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (error) {
    throw new Error(error);
  }
}

function usePut(siteId: number, credentialId?: number) {
  const queryClient = useQueryClient();

  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    async (body: UpdateUtilityCredentialRequest, checkFeatureAccessHeader) => {
      if (credentialId === undefined) {
        return;
      }
      return putCredential(
        siteId,
        credentialId,
        body,
        checkFeatureAccessHeader
      );
    },
    {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: [UTILITY_CREDENTIALS_KEY, siteId],
        }),
      onError: (e) => {
        throw new Error(
          `Unable to update utility credential for site: ${siteId}
        ${(e as Error)?.message || "Unknown Error"}`
        );
      },
    }
  );
}

// endpoint requires {feature: "Utilities", access: "edit"}
async function deleteCredential(
  siteId: number,
  credentialId: number,
  deleteSharedCredentials: boolean,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { error, response } = await FetchClients.utilities.DELETE(
    "/{site_id}/credentials/{credential_id}",
    {
      params: {
        query: {
          delete_shared_credentials: deleteSharedCredentials,
        },
        path: {
          site_id: siteId,
          credential_id: credentialId,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );

  if (response.status === 412) {
    throw new Error("Credential already has accounts associated with it");
  }

  if (error) {
    throw new Error(error);
  }
}

function useDelete(siteId: number, credentialId: number) {
  const queryClient = useQueryClient();

  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (
      { deleteSharedCredentials }: { deleteSharedCredentials?: boolean } = {
        deleteSharedCredentials: false,
      },
      checkFeatureAccessHeader
    ) =>
      deleteCredential(
        siteId,
        credentialId,
        deleteSharedCredentials ?? false,
        checkFeatureAccessHeader
      ),
    {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: [UTILITY_CREDENTIALS_KEY, siteId],
        }),
      onError: (e) => {
        throw new Error(
          `Unable to delete utility credential: ${
            (e as Error)?.message || "Unknown Error"
          }`
        );
      },
    }
  );
}

// endpoint requires {feature: "Utilities", access: "read"}
async function getCredentialAccounts(
  siteId: number,
  credentialId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.utilities.GET(
    "/{site_id}/credentials/{credential_id}/accounts",
    {
      params: {
        path: {
          site_id: siteId,
          credential_id: credentialId,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (data) return data;
  throw new Error(error);
}

// After a credential is successfully added, accounts to select can be queried
function useQueryAccounts(siteId: number, credentialId: number) {
  return useQueryWithAccessCheckLog(
    ["Utilities", "read"],
    (checkFeatureAccessHeader) =>
      getCredentialAccounts(siteId, credentialId, checkFeatureAccessHeader),
    {
      queryKey: [UTILITY_CREDENTIALS_KEY, siteId, credentialId],
    }
  );
}

// endpoint requires {feature: "Utilities", access: "edit"}
async function postCredentialAccounts(
  siteId: number,
  credentialId: number,
  body: UtilityAccountRequest[],
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { error } = await FetchClients.utilities.POST(
    "/{site_id}/credentials/{credential_id}/accounts",
    {
      body,
      params: {
        path: {
          site_id: siteId,
          credential_id: credentialId,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (!error) return;
  throw new Error(error);
}

function usePostAccounts(siteId: number, credentialId: number) {
  const queryClient = useQueryClient();

  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (body: UtilityAccountRequest[], checkFeatureAccessHeader) =>
      postCredentialAccounts(
        siteId,
        credentialId,
        body,
        checkFeatureAccessHeader
      ),
    {
      onSuccess: () => {
        queryClient.removeQueries({
          queryKey: [UTILITY_CREDENTIALS_KEY, siteId, credentialId],
        });
        queryClient.invalidateQueries({
          queryKey: [UTILITY_ACCOUNTS_KEY, siteId],
        });
      },
      onError: (e) => {
        throw new Error(
          `Unable to update accounts for site: ${siteId} and credential: ${credentialId}
        ${(e as Error)?.message || "Unknown Error"}`
        );
      },
    }
  );
}
